import { ChatCircleDotsIcon, GiftIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SHOWCASE_SORTS, type SearchResultOpenData, type ShowcaseSort } from '@entities/search-run';
import { useFocusKarl } from '@shared/lib/appShell';
import { Button, KarlIntro, Select, Skeleton } from '@shared/ui';
import { Reveal } from '@shared/ui/Reveal/Reveal';

import { FavoritesEmpty, FavoritesFilterButton, FavoritesOnlyNote, InlineHeart } from './Favorites';
import { LockedTop4 } from './LockedTop4';
import styles from './Showcase.module.css';
import { ShowcaseCard } from './ShowcaseCard';

/** Значение «случайный порядок» в списке сортировки: параметра `sort` нет. */
const RANDOM_ORDER = '';

interface ShowcaseProps {
  searchId: string;
  runId: string;
  /** Карточки текущей страницы — только `open`. */
  items: SearchResultOpenData[];
  /**
   * Размер рабочего набора целиком — не страницы и не «Meine Favoriten».
   * `undefined` — ещё не известен: числа в репликах не выдумываются.
   */
  setSize: number | undefined;
  /** Отмеченные автомобили набора — `favorites_count` из ответа. */
  favoritesCount: number;
  sort: ShowcaseSort | undefined;
  favoritesOnly: boolean;
  /** `empty_reason = NO_FAVORITES`: «Meine Favoriten» включено, отметок нет. */
  noFavorites: boolean;
  /** Строка запроса карточки автомобиля: прогон, сортировка, страница. */
  vehicleLinkSearch: string;
  /** Адрес карточки автомобиля в заказе. */
  vehicleHref: (vehicleId: string) => string;
  busy?: boolean | undefined;
  /** Пагинация экрана — та же, что у полного списка. */
  pagination?: ReactNode | undefined;
  onSortChange: (sort: ShowcaseSort | undefined) => void;
  onFavoritesOnlyChange: (next: boolean) => void;
}

/**
 * Открытая витрина рабочего набора до оплаты (Epic 58, FE-586, макет 03,
 * DESIGN_SYSTEM §5.5, §7).
 *
 * Сверху вниз: реплика Karl, плашка «kostenlos», сортировка, сетка открытых
 * карточек, пагинация, вторая реплика Karl, карточка следующего шага с
 * единственной красной кнопкой и нижняя строка действий.
 *
 * Чего здесь нет — и это не упрощение, а граница бесплатного:
 *
 * * **ранжирования.** Ни «Beste Treffer», ни номеров мест: порядок по
 *   умолчанию — детерминированная перестановка прогона, и он **назван
 *   случайным**; сортировки — только по открытым значениям (BE-580);
 * * **фильтров, карты и расстояния.** Сужение — через Karl и новый прогон
 *   («Suche anpassen»); координат нет ни у автомобилей, ни у пользователя;
 * * **аналитики на карточках.** Match Score, причины, рынок и признаки
 *   внимания не приходят с сервера вовсе (§34.11).
 *
 * Все числа — из ответа API, цена услуги — из конфигурации (`LockedTop4`).
 */
export function Showcase({
  searchId,
  runId,
  items,
  setSize,
  favoritesCount,
  sort,
  favoritesOnly,
  noFavorites,
  vehicleHref,
  vehicleLinkSearch,
  busy = false,
  pagination,
  onSortChange,
  onFavoritesOnlyChange,
}: ShowcaseProps) {
  const { t } = useTranslation();
  const focusKarl = useFocusKarl();

  const showAll = () => {
    onFavoritesOnlyChange(false);
  };

  return (
    <section className={styles.wrap} aria-labelledby="showcase-title">
      <h2 id="showcase-title" className="visually-hidden">
        {t('showcase.title')}
      </h2>

      {setSize === undefined ? (
        <div className={styles.introSkeleton} aria-hidden="true">
          <Skeleton height="4.5rem" radius="var(--radius-card)" />
          <Skeleton height="4rem" radius="var(--radius-card)" />
        </div>
      ) : (
        <>
          {/* Реплика Karl — фиксированный текст из локалей (DESIGN_SYSTEM §5.4),
              число — размер набора из API. */}
          <KarlIntro>
            <strong>{t('showcase.karl.found', { count: setSize })}</strong>
            <br />
            {t('showcase.karl.foundText', { count: setSize })}
          </KarlIntro>

          <div className={styles.free}>
            <GiftIcon size={28} className={styles.freeIcon} aria-hidden="true" />
            <div className={styles.freeText}>
              <p className={styles.freeTitle}>{t('showcase.free.title', { count: setSize })}</p>
              <p className={styles.freeLead}>{t('showcase.free.text')}</p>
            </div>
            <p className={styles.counter}>
              <span className={`${styles.counterValue} tabular`}>
                {t('showcase.free.counter', { count: setSize, total: setSize })}
              </span>
              <span className={styles.counterLabel}>{t('showcase.free.counterLabel')}</span>
            </p>
          </div>
        </>
      )}

      <div className={styles.toolbar}>
        <Select
          label={t('showcase.sort.label')}
          className={styles.sort}
          value={sort ?? RANDOM_ORDER}
          onChange={(event) => {
            const value = event.target.value;
            onSortChange(
              (SHOWCASE_SORTS as readonly string[]).includes(value)
                ? (value as ShowcaseSort)
                : undefined,
            );
          }}
        >
          <option value={RANDOM_ORDER}>{t('showcase.sort.random')}</option>
          {SHOWCASE_SORTS.map((option) => (
            <option key={option} value={option}>
              {t(`showcase.sort.${option}`)}
            </option>
          ))}
        </Select>
        {/* Порядок по умолчанию назван случайным прямо: витрина — не рейтинг
            и не «лучшие варианты» (DESIGN_SYSTEM §8). */}
        {sort === undefined ? <p className={styles.note}>{t('showcase.orderIsRandom')}</p> : null}
      </div>

      {favoritesOnly ? <FavoritesOnlyNote onShowAll={showAll} /> : null}

      {noFavorites ? (
        <FavoritesEmpty onShowAll={showAll} />
      ) : (
        <ul className={styles.grid} aria-busy={busy}>
          {items.map((item, index) => (
            <Reveal as="li" key={item.vehicle_id} index={index % 3}>
              <ShowcaseCard
                searchId={searchId}
                runId={runId}
                item={item}
                index={index}
                to={`${vehicleHref(item.vehicle_id)}${vehicleLinkSearch}`}
              />
            </Reveal>
          ))}
        </ul>
      )}

      {pagination}

      {setSize === undefined ? null : (
        <KarlIntro>
          <strong>{t('showcase.karl.more', { count: setSize })}</strong>
          <br />
          <Trans i18nKey="showcase.karl.moreText" components={{ heart: <InlineHeart /> }} />
        </KarlIntro>
      )}

      <LockedTop4 searchId={searchId} />

      <div className={styles.actions}>
        <FavoritesFilterButton
          count={favoritesCount}
          active={favoritesOnly}
          onChange={onFavoritesOnlyChange}
        />
        {/* Обе кнопки ведут к полю ввода Karl в рельсе (на телефоне — в листе
            Karl): уточнение запроса пересобирает подбор (MVP_PRD §35.4), а
            экрана диалога, куда можно было бы «уйти», здесь нет (CLAUDE.md §8).
            «Suche anpassen» ещё и начинает сообщение за человека — в пустом
            поле; набранный черновик не трогается (FE-587). */}
        {focusKarl ? (
          <>
            <Button
              variant="outline-action"
              onClick={() => {
                focusKarl(t('showcase.actions.adjustPrefill'));
              }}
            >
              <span className={styles.actionLabel}>
                <MagnifyingGlassIcon size={16} aria-hidden="true" />
                {t('showcase.actions.adjust')}
              </span>
            </Button>
            <Button
              variant="outline-action"
              onClick={() => {
                focusKarl();
              }}
            >
              <span className={styles.actionLabel}>
                <ChatCircleDotsIcon size={16} aria-hidden="true" />
                {t('showcase.actions.talk')}
              </span>
            </Button>
          </>
        ) : null}
      </div>
    </section>
  );
}
